import { db } from "../../firebase";
import {
	collection,
	DocumentReference,
	doc,
	query,
	addDoc,
	updateDoc,
	getDoc,
	getDocs,
	orderBy,
	where,
	Timestamp,
	deleteDoc
} from "firebase/firestore";

interface DataType {
	id?: string;
	created?: Timestamp | String; // Allow both Timestamp and string
	is_released_independent?: Boolean;
	label?: String;
	modified?: Timestamp | String;
	notes?: String;
	release_date?: Timestamp | String;
	release_id?: String;
	release_status?: String;
	song?: String;
	user_id?: String; // Assuming you have a 'user' field
}

interface SearchType {
	id: string;
	name: String;
}

const searchRecordsSong = async (currentUser: any, searchField: string, searchTerm: String): Promise<SearchType[]> => {
	try {
		const searchCollection = collection(db, "catalog");
		const q = query(
			searchCollection,
			where("user_id", "==", currentUser.uid),
			where(searchField, ">=", searchTerm),
			where(searchField, "<=", searchTerm + "\uf8ff"),
			orderBy(searchField, "asc")
		);
		const data: SearchType[] = [];
		const snapshot = await getDocs(q);
		console.log("snapshot size: " + snapshot.size);
		snapshot.forEach((doc) => {
			const songName = doc.data().song_name;
			console.log("song name: " + songName);
			data.push({
				id: doc.id,
				name: songName || ""
			});
		});
		return data;
	} catch (e) {
		console.log("Error searching songs: " + String(e));
		return [];
	}
};

const searchRecordsLabel = async (currentUser: any, searchField: string, searchTerm: String): Promise<SearchType[]> => {
	try {
		const searchCollection = collection(db, "labels");
		const q = query(
			searchCollection,
			where("user_id", "==", currentUser.uid),
			where(searchField, ">=", searchTerm),
			where(searchField, "<=", searchTerm + "\uf8ff"),
			orderBy(searchField, "asc")
		);
		const data: SearchType[] = [];
		const snapshot = await getDocs(q);
		console.log("snapshot size: " + snapshot.size);
		snapshot.forEach((doc) => {
			const labelName = doc.data().label_name;
			data.push({
				id: doc.id,
				name: labelName || ""
			});
		});
		return data;
	} catch (e) {
		console.log("Error searching labels: " + String(e));
		return [];
	}
};

const viewRecords = async (currentUser: any): Promise<DataType[]> => {
	try {
		const q = query(collection(db, "releases"), where("user_id", "==", currentUser.uid), orderBy("created", "desc"));
		const querySnapshot = await getDocs(q);
		const data: DataType[] = [];
		querySnapshot.forEach((doc) => {
			const docData = doc.data();
			const createdDate = getFormattedDate(docData.created);
			const modifiedDate = getFormattedDate(docData.modified);
			const releaseDate = getFormattedDate(docData.release_date);
			data.push({
				id: doc.id,
				created: createdDate || "",
				is_released_independent: docData.is_released_independant || "",
				label: docData.label || "",
				modified: modifiedDate || "",
				notes: docData.notes || "",
				release_date: releaseDate || "",
				release_id: docData.release_id || "",
				release_status: docData.release_status || "",
				song: docData.song || ""
			});
		});
		return data;
	} catch (error) {
		console.error("Error fetching Fan data:", error);
		return [];
	}
};

const viewRecord = async (recordId: string) => {
	try {
		const docRef = doc(db, "releases", recordId);
		const docSnap = await getDoc(docRef);
		if (docSnap.exists()) {
			let data = docSnap.data();
			const documentId = docSnap.id;
			data = { ...data, id: documentId };
			const songId = data.song; // Assuming 'song' field contains the song ID (string)

			let referencedDocument = null;
			if (songId) {
				referencedDocument = await viewReferenceRecord(songId);
			}
			return { mainDocument: data, referencedDocument: referencedDocument };
		} else {
			return null;
		}
	} catch (error) {
		console.error("Error viewing release: ", error);
		return null;
	}
};

const viewReferenceRecord = async (reference: DocumentReference) => {
	try {
		const docSnap = await getDoc(reference);
		if (docSnap.exists()) {
			return {
				id: docSnap.id,
				...docSnap.data()
			};
		} else {
			return null;
		}
	} catch (error) {
		return null;
	}
};

async function addRecord(newRecord) {
	try {
		await addDoc(collection(db, "releases"), newRecord);
	} catch (error) {
		console.error("Error adding release: ", error);
	}
}

export const editRecord = async (documentId: string, updates: { [key: string]: any }) => {
	try {
		const docRef = doc(db, "releases", documentId);
		await updateDoc(docRef, updates);
	} catch (error) {
		throw error;
	}
};

const deleteRecord = async (documentId) => {
	try {
		const docRef = doc(db, "releases", documentId);
		await deleteDoc(docRef);
	} catch (error) {
		console.error("Error removing release: ", error);
	}
};

export const getFormattedDate = (date: Timestamp | string | undefined): string => {
	if (!date) return "";
	const dateObj = typeof date === "string" ? new Date(date) : date.toDate();
	return dateObj.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) + " " + dateObj.toLocaleDateString("en-US");
};

const addUserRef = (userId: string) => {
	return doc(db, "users", userId);
};

export { addRecord, viewRecords, searchRecordsSong, searchRecordsLabel, viewRecord, viewReferenceRecord, deleteRecord };
